import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import EditIcon from "@mui/icons-material/Edit";
import { validateTariffData } from "../../../../../../../../../../services/tariffeAnalysisService";

const TabValidazione = ({ tariffe, onUpdateTariff }) => {
  const [filterType, setFilterType] = useState("all"); // all, errors, warnings, review

  // Calculate validation results for all tariffs
  const tariffeWithValidation = useMemo(() => {
    return tariffe.map((tariff) => {
      const validation = validateTariffData(tariff);
      return {
        ...tariff,
        validation,
      };
    });
  }, [tariffe]);

  // Apply filters
  const filteredTariffe = useMemo(() => {
    switch (filterType) {
      case "errors":
        return tariffeWithValidation.filter(
          (t) => t.validation.errors.length > 0
        );
      case "warnings":
        return tariffeWithValidation.filter(
          (t) => t.validation.warnings.length > 0
        );
      case "review":
        return tariffeWithValidation.filter((t) => t.validation.needsReview);
      default:
        return tariffeWithValidation;
    }
  }, [tariffeWithValidation, filterType]);

  // Statistics
  const stats = useMemo(() => {
    return {
      total: tariffeWithValidation.length,
      valid: tariffeWithValidation.filter((t) => t.validation.isValid).length,
      errors: tariffeWithValidation.filter((t) => t.validation.errors.length > 0)
        .length,
      warnings: tariffeWithValidation.filter(
        (t) => t.validation.warnings.length > 0
      ).length,
      needsReview: tariffeWithValidation.filter((t) => t.validation.needsReview)
        .length,
    };
  }, [tariffeWithValidation]);

  const getSeverityIcon = (validation) => {
    if (validation.errors.length > 0) {
      return <ErrorIcon color="error" />;
    }
    if (validation.warnings.length > 0) {
      return <WarningIcon color="warning" />;
    }
    return <CheckCircleIcon color="success" />;
  };

  const getSeverityChip = (validation) => {
    if (validation.errors.length > 0) {
      return <Chip label="Errori" color="error" size="small" />;
    }
    if (validation.warnings.length > 0) {
      return <Chip label="Attenzione" color="warning" size="small" />;
    }
    return <Chip label="Valida" color="success" size="small" />;
  };

  const handleMarkAsReviewed = (id) => {
    const tariff = tariffe.find((t) => t.id === id);
    if (tariff) {
      onUpdateTariff(id, {
        ...tariff,
        needsReview: false,
        confidenceScore: Math.max(tariff.confidenceScore, 80),
      });
    }
  };

  return (
    <Box>
      {/* Statistics Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1, textAlign: "center" }}>
          <Typography variant="h4" color="primary">
            {stats.total}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Totale Tariffe
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: "center" }}>
          <Typography variant="h4" color="success.main">
            {stats.valid}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Valide
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: "center" }}>
          <Typography variant="h4" color="error.main">
            {stats.errors}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Con Errori
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: "center" }}>
          <Typography variant="h4" color="warning.main">
            {stats.warnings}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Con Avvisi
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: "center" }}>
          <Typography variant="h4" color="info.main">
            {stats.needsReview}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Da Revisionare
          </Typography>
        </Paper>
      </Box>

      {/* Filter */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filtra per</InputLabel>
          <Select
            value={filterType}
            label="Filtra per"
            onChange={(e) => setFilterType(e.target.value)}
          >
            <MenuItem value="all">Tutte le tariffe</MenuItem>
            <MenuItem value="errors">Solo con errori</MenuItem>
            <MenuItem value="warnings">Solo con avvisi</MenuItem>
            <MenuItem value="review">Da revisionare</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {filteredTariffe.length} risultati
        </Typography>
      </Box>

      {/* Validation Results */}
      {filteredTariffe.length === 0 ? (
        <Alert severity="info">
          {filterType === "all"
            ? "Nessuna tariffa da validare."
            : "Nessuna tariffa corrisponde ai filtri selezionati."}
        </Alert>
      ) : (
        <Box>
          {filteredTariffe.map((tariff) => (
            <Accordion key={tariff.id} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  {getSeverityIcon(tariff.validation)}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {tariff.fornitore} - {tariff.nomeOfferta}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tariff.tipo.toUpperCase()} | Confidence: {tariff.confidenceScore}%
                    </Typography>
                  </Box>
                  {getSeverityChip(tariff.validation)}
                  {tariff.validation.needsReview && (
                    <Chip label="Revisione Richiesta" color="info" size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Errors */}
                  {tariff.validation.errors.length > 0 && (
                    <Alert severity="error">
                      <Typography variant="subtitle2" fontWeight={600}>
                        Errori Rilevati:
                      </Typography>
                      <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                        {tariff.validation.errors.map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}

                  {/* Warnings */}
                  {tariff.validation.warnings.length > 0 && (
                    <Alert severity="warning">
                      <Typography variant="subtitle2" fontWeight={600}>
                        Avvisi:
                      </Typography>
                      <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                        {tariff.validation.warnings.map((warning, idx) => (
                          <li key={idx}>{warning}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}

                  {/* Tariff Details Table */}
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography variant="subtitle2">Dettagli Tariffa</Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, width: "30%" }}>
                            Fornitore
                          </TableCell>
                          <TableCell>{tariff.fornitore}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Nome Offerta</TableCell>
                          <TableCell>{tariff.nomeOfferta}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                          <TableCell>{tariff.tipo}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Prezzo Materia Prima
                          </TableCell>
                          <TableCell>
                            {tariff.prezzoMateriaPrima?.valore}{" "}
                            {tariff.prezzoMateriaPrima?.unita}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Spread</TableCell>
                          <TableCell>
                            {tariff.spread ? `€ ${tariff.spread}` : "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>PCV Mensile</TableCell>
                          <TableCell>
                            {tariff.pcv?.valoreMensile
                              ? `€ ${tariff.pcv.valoreMensile}`
                              : "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>PCV Annuo</TableCell>
                          <TableCell>
                            {tariff.pcv?.valoreAnnuo
                              ? `€ ${tariff.pcv.valoreAnnuo}`
                              : "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Confidence Score</TableCell>
                          <TableCell>
                            <Chip
                              label={`${tariff.confidenceScore}%`}
                              size="small"
                              color={
                                tariff.confidenceScore >= 80
                                  ? "success"
                                  : tariff.confidenceScore >= 70
                                  ? "warning"
                                  : "error"
                              }
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Actions */}
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    {tariff.validation.needsReview && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleMarkAsReviewed(tariff.id)}
                      >
                        Marca come Revisionata
                      </Button>
                    )}
                    <Tooltip title="Modifica tariffa">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => {
                          // You can implement edit functionality here
                          alert("Vai al Tab 'Tabelle Impostazioni' per modificare questa tariffa");
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TabValidazione;
